/**
 * AdminDashboard Page
 * Overview page with stats and recent activity
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  TrendingUp as TrendingUpIcon,
  Link as LinkIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import StatsCard from '../../components/admin/StatsCard';
import UserStatusBadge from '../../components/admin/UserStatusBadge';
import { useApi } from '../../hooks/useApi';
import { format } from 'date-fns';

interface Stats {
  totalUsers: number;
  activeUsers: number;
  newThisWeek: number;
  newToday: number;
  totalConnections: number;
  recentRegistrations: Array<{
    id: string;
    email: string;
    full_name: string | null;
    created_at: string;
    is_active: boolean;
    is_admin: boolean;
  }>;
}

const AdminDashboard: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM d, yyyy HH:mm');
  };

  const calculateGrowth = () => {
    if (!stats || stats.totalUsers === 0) return 0;
    const growthRate = (stats.newThisWeek / (stats.totalUsers - stats.newThisWeek)) * 100;
    return Math.round(growthRate * 10) / 10;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Platform overview and user management
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            icon={<PeopleIcon fontSize="large" />}
            loading={loading}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active Users"
            value={stats?.activeUsers || 0}
            subtitle="Last 7 days"
            icon={<TrendingUpIcon fontSize="large" />}
            loading={loading}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="New This Week"
            value={stats?.newThisWeek || 0}
            trend={{
              value: calculateGrowth(),
              label: 'growth',
            }}
            icon={<PersonAddIcon fontSize="large" />}
            loading={loading}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Connections"
            value={stats?.totalConnections || 0}
            subtitle="OAuth services"
            icon={<LinkIcon fontSize="large" />}
            loading={loading}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Recent Registrations */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" fontWeight={600}>
            Recent Registrations
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/admin/users')}
          >
            View All Users
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats?.recentRegistrations.map((user) => (
                <TableRow 
                  key={user.id}
                  sx={{ 
                    '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' },
                  }}
                  onClick={() => navigate('/admin/users')}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{user.email}</Typography>
                      {user.is_admin && (
                        <UserStatusBadge type="admin" value={true} size="small" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{user.full_name || '-'}</TableCell>
                  <TableCell>
                    <UserStatusBadge type="active" value={user.is_active} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(user.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {(!stats || stats.recentRegistrations.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No recent registrations
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={3}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/admin/users')}
              startIcon={<PeopleIcon />}
            >
              Manage Users
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/llm-monitoring')}
              startIcon={<TrendingUpIcon />}
            >
              LLM Monitoring
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/admin/connectors')}
              startIcon={<ExtensionIcon />}
            >
              Manage Connectors
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default AdminDashboard;